import { useMemo, useCallback, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router'

/**
 * Manages a scoped set of URL search parameters with defaults, validation, and cleanup.
 *
 * TWO-PHASE INITIALIZATION:
 * This hook uses a two-phase initialization pattern to handle cases where `defaults`
 * may be empty initially (while data loads) and then populated later:
 *
 * Phase 1 (Initialization): When `defaults` is empty `{}`
 *   - Reads URL params, validates them, returns normalized values
 *   - Does NOT update URL (preserves user's bookmarked params)
 *   - This prevents losing URL params while `managedParamKeys` populates
 *
 * Phase 2 (Management): When `defaults` is populated
 *   - Normal URL sync: adds defaults for missing params, validates existing, cleans up invalid
 *   - Updates URL as needed
 *
 * IMPORTANT: URL updates only happen when `defaults` is populated. If your `defaults`
 * is empty, the hook is in initialization mode and will preserve all URL params.
 *
 * Example scenario:
 * - First render: `managedParamKeys = []` → `defaults = {}` → Phase 1 (read-only)
 * - Later render: `managedParamKeys = ['sortKey']` → `defaults = {sortKey: 'schools'}` → Phase 2 (management)
 *
 * RESPONSIBILITIES:
 * - Sets default values for managed params (if missing/invalid)
 * - Validates and normalizes params using validateParam function
 * - Prunes params in scopedParams but not in defaults (cleanup)
 * - Updates URL in a single pass (no flickering)
 * - Calls onParamsChange when params change
 *
 * RELATIONSHIP:
 * - Lower-level hook for URL search param management
 * - Used by useValidatedTableParams for URL updates
 * - Can be used standalone for simple URL param management
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.defaults - Managed params with default values.
 *   Example: { sortKey: 'schools', orderBy: 'asc', currentPage: 1 }
 *   These params will always be in the URL (with default if missing).
 *   IMPORTANT: If `defaults` is empty `{}`, the hook enters initialization mode
 *   and will NOT update the URL (preserves existing params).
 *   URL updates only occur when `defaults` is populated.
 * @param {Array<string>} config.scopedParams - All params this hook manages (for cleanup).
 *   Example: ['sortKey', 'orderBy', 'currentPage', 'filter']
 *   Params in this array but not in defaults will be removed from URL (during Phase 2 only).
 *   Params NOT in this array will be left untouched (allows multiple hooks to coexist).
 * @param {Function} [config.validateParam] - Validation and normalization function.
 *   Signature: (key, value) => normalizedValue
 *   - Returns normalized value (clamped, type-converted, etc.)
 *   - If value is invalid, should return the default for that key
 *   - Example: validateParam('currentPage', '999') => 1 (clamped to min)
 * @param {boolean} [config.shouldSetDefaults=true] - Whether to set defaults in URL.
 *   If false, defaults won't be added to URL but will still be returned in managedParams.
 *   Note: During Phase 1 (empty defaults), this has no effect as URL updates are skipped.
 * @param {Function} [config.onParamsChange] - Callback when managedParams change.
 *   Signature: (managedParams: Object) => void
 *   Receives normalized params (from validateParam).
 *   Only fires when params actually change.
 * @param {boolean} [config.isInitializing] - Whether the hook is in initialization phase.
 *   If `defaults` is empty and `isInitializing` is `true` (default), Phase 1 (preserve params).
 *   If `defaults` is empty and `isInitializing` is `false`, treat as intentional empty (cleanup).
 *   If `defaults` is populated, this parameter is ignored (Phase 2).
 *   Default: `true` when `defaults` is empty, `false` otherwise.
 * @param {boolean} [config.isSettled] - Whether `defaults` has reached its final state and won't grow.
 *   When `true`, removes params in `scopedParams` but not in `defaults` (even if never in `defaults`).
 *   When `false` or `undefined`, preserves params that were never in `defaults` (might become managed).
 *   Use this when data is hydrated and `managedParamKeys` is stable (e.g., `Array.isArray(data)`).
 * @returns {Object} - Returns managed params and update function:
 *   - managedParams: Object with all params from defaults (normalized values)
 *   - updateUrlParams: Function to update URL params
 *     Signature: (updates: Object, method?: 'replace' | 'push') => void
 *   - searchParams: URLSearchParams object for direct URL reading
 *
 * @example
 * // Simple usage: manage sort params
 * const { managedParams, updateUrlParams } = useManagedUrlSearchParams({
 *   defaults: { sortKey: 'schools', orderBy: 'asc' },
 *   scopedParams: ['sortKey', 'orderBy'],
 * })
 *
 * @example
 * // With validation: clamp page numbers
 * const { managedParams } = useManagedUrlSearchParams({
 *   defaults: { currentPage: 1 },
 *   scopedParams: ['currentPage', 'pageSize'],
 *   validateParam: (key, value) => {
 *     if (key === 'currentPage') {
 *       const num = parseInt(value, 10)
 *       return isNaN(num) || num < 1 ? 1 : num
 *     }
 *     return value
 *   },
 * })
 *
 * @example
 * // Two-phase initialization: defaults starts empty, then populates
 * // Phase 1: defaults = {} → URL params preserved (read-only)
 * // Phase 2: defaults = {sortKey: 'schools'} → URL sync begins
 * const { managedParams } = useManagedUrlSearchParams({
 *   defaults: managedParamKeys.length > 0 ? { sortKey: 'schools' } : {},
 *   scopedParams: ['sortKey', 'orderBy'],
 * })
 */
export const useManagedUrlSearchParams = ({
  defaults = {},
  scopedParams = [],
  validateParam,
  shouldSetDefaults = true,
  onParamsChange,
  isInitializing: explicitIsInitializing,
  isSettled = false,
} = {}) => {
  // ============================================
  // SETUP: React Router hooks
  // ============================================
  const history = useHistory()
  const location = useLocation()

  // ============================================
  // TWO-PHASE INITIALIZATION: Track if defaults was empty
  // ============================================
  // This ref tracks whether defaults was empty on the previous render
  // Used to detect Phase 1 (initialization) vs Phase 2 (management)
  const wasDefaultsEmptyRef = useRef(true) // Assume empty initially
  // Track if this is the first render with populated defaults
  // Used to preserve valid URL params on first render (prevent overwriting user's bookmarked params)
  const isFirstRenderWithDefaultsRef = useRef(true)
  // Track previous defaults keys to detect when defaults grows (new keys added)
  // Only remove params that were previously in defaults (previously managed)
  // Preserve params that are in scopedParams but were never in defaults (might become managed)
  const previousDefaultsKeysRef = useRef(new Set())

  // ============================================
  // DERIVED VALUES: Parse URL and merge with defaults
  // ============================================
  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ])

  // Read managed params with defaults (only includes keys from defaults)
  // Values are normalized if validateParam is provided
  const managedParams = useMemo(() => {
    const result = {}
    Object.keys(defaults).forEach(key => {
      const defaultValue = defaults[key]
      const urlValue = searchParams.get(key)

      if (urlValue !== null) {
        // Validate and normalize if validateParam provided
        if (validateParam) {
          result[key] = validateParam(key, urlValue)
        } else {
          // Basic type conversion if no validator
          // TODO: build out all type conversions to use here - bool, number, string, array, object, etc.
          if (typeof defaultValue === 'number') {
            result[key] = parseInt(urlValue, 10) || defaultValue
          } else {
            result[key] = urlValue
          }
        }
      } else {
        // No URL value - use default
        result[key] = defaultValue
      }
    })

    return result
  }, [searchParams, defaults, validateParam])

  // ============================================
  // FUNCTIONS: URL update utilities
  // ============================================
  // Convenience function to update URL managed params
  // Only updates params that are in defaults (scoped to managed params)
  // Preserves unmanaged params (not in defaults or scopedParams)
  const updateUrlParams = useCallback(
    (updates, method = 'replace') => {
      const currentSearchParams = new URLSearchParams(location.search)
      const newSearchParams = new URLSearchParams()
      let hasChanges = false
      const processedKeys = new Set()

      // First, rebuild managed params in defaults order
      Object.keys(defaults).forEach(key => {
        processedKeys.add(key)

        if (key in updates) {
          const value = updates[key]
          const newValue =
            value !== undefined && value !== null ? value.toString() : null

          if (newValue !== null) {
            const currentValue = currentSearchParams.get(key)
            if (currentValue !== newValue) {
              newSearchParams.set(key, newValue)
              hasChanges = true
            } else {
              newSearchParams.set(key, newValue)
            }
          } else {
            // Removing a value
            if (currentSearchParams.has(key)) {
              hasChanges = true
            }
          }
        } else {
          // Keep existing value if present
          const currentValue = currentSearchParams.get(key)
          if (currentValue !== null) {
            newSearchParams.set(key, currentValue)
          }
        }
      })

      // Preserve unmanaged params (not in defaults or scopedParams)
      currentSearchParams.forEach((value, key) => {
        if (!processedKeys.has(key) && !scopedParams.includes(key)) {
          newSearchParams.set(key, value)
        }
      })

      if (hasChanges) {
        const navigateMethod =
          method === 'push' ? history.push : history.replace
        // TODO: replace with navigationEvent
        navigateMethod({ search: newSearchParams.toString() })
      }
    },
    [location.search, history, defaults, scopedParams],
  )

  // ============================================
  // SIDE EFFECTS: URL synchronization (single pass)
  // - Set defaults for managed params
  // - Validate and normalize managed params
  // - Prune unmanaged params in scope
  // ============================================
  useEffect(() => {
    const currentUrlParams = new URLSearchParams(location.search)
    const updates = {}
    let hasChanges = false

    // ============================================
    // TWO-PHASE INITIALIZATION PATTERN
    // ============================================
    // Phase 1: Initialization - defaults is empty (data still loading, managedParamKeys not populated)
    //   - Read URL params, validate them, return values
    //   - DO NOT update URL (preserve user's bookmarked params)
    // Phase 2: Management - defaults is populated (data loaded, managedParamKeys determined)
    //   - Normal URL sync: add defaults for missing params, validate existing, cleanup invalid
    // Intentional Empty: defaults is empty AND isInitializing is false (component intentionally wants no params)
    //   - Remove all scoped params from URL
    //
    // This prevents losing URL params during initialization when managedParamKeys grows
    // (e.g., sortKey/orderBy not in defaults initially, but added when needsSorting becomes true)
    // ============================================
    const isDefaultsEmpty = Object.keys(defaults).length === 0
    // Determine if we're initializing:
    // - If explicitIsInitializing is provided, use it
    // - Otherwise, default to true if defaults was empty before AND is still empty now
    const isInitializing =
      explicitIsInitializing !== undefined
        ? explicitIsInitializing
        : wasDefaultsEmptyRef.current && isDefaultsEmpty

    if (isInitializing) {
      wasDefaultsEmptyRef.current = isDefaultsEmpty
      return // Skip URL updates during initialization
    }

    // Phase 2: Management - defaults is populated, we can manage URL
    // OR Intentional Empty: defaults is empty AND isInitializing is explicitly false
    // OR Intentional Empty: defaults was populated, now empty (cleanup)
    // Store previous state BEFORE updating ref (needed for intentional cleanup detection)
    const wasDefaultsEmptyBefore = wasDefaultsEmptyRef.current
    wasDefaultsEmptyRef.current = isDefaultsEmpty

    // Check for intentional empty cases:
    // 1. defaults is empty AND isInitializing was explicitly set to false
    // 2. defaults was populated before, now empty (transition from populated to empty)
    const isIntentionalEmpty =
      (isDefaultsEmpty && explicitIsInitializing === false) ||
      (isDefaultsEmpty && !wasDefaultsEmptyBefore)

    // Check if this is the first render with populated defaults
    // On first render, we should preserve existing valid URL params (don't overwrite user's bookmarked params)
    const isFirstRenderWithDefaults =
      isFirstRenderWithDefaultsRef.current && !isDefaultsEmpty
    if (isFirstRenderWithDefaults) {
      isFirstRenderWithDefaultsRef.current = false
    }

    // Handle both boolean and function forms of shouldSetDefaults
    const shouldSet =
      typeof shouldSetDefaults === 'function'
        ? shouldSetDefaults()
        : shouldSetDefaults !== false

    // Step 1: Set defaults and validate managed params
    if (shouldSet && Object.keys(defaults).length > 0) {
      Object.keys(defaults).forEach(key => {
        const urlValue = currentUrlParams.get(key)
        const defaultValue = defaults[key]
        const hasParam = currentUrlParams.has(key)

        // Determine the normalized value
        let normalizedValue
        if (!hasParam) {
          // Param missing - use default
          normalizedValue = defaultValue
        } else if (validateParam) {
          // Param exists - validate and normalize
          normalizedValue = validateParam(key, urlValue)
        } else {
          // No validator - use URL value as-is
          normalizedValue = urlValue
        }

        // Check if value needs updating
        // Only update if param is missing OR normalized value differs from URL value
        const currentValue = currentUrlParams.get(key)

        // Convert both to strings for comparison (handles numbers, null, undefined)
        const normalizedValueStr =
          normalizedValue !== null && normalizedValue !== undefined
            ? String(normalizedValue)
            : ''
        const currentValueStr = currentValue ? String(currentValue) : ''

        // Always fix invalid values (overwrite junk with normalized)
        // Also add missing params
        const needsUpdate = !hasParam || currentValueStr !== normalizedValueStr

        if (needsUpdate) {
          // Only set update if normalized value is not null/undefined (preserve URL if invalid becomes null)
          if (normalizedValue !== null && normalizedValue !== undefined) {
            updates[key] = normalizedValueStr
            hasChanges = true
          } else if (!hasParam) {
            // Param was missing and normalized to null/undefined - still need to track this
            hasChanges = true
          }
        }
      })
    }

    // Step 2: Prune params in scopedParams but not in defaults
    // This removes params that were managed but are no longer in defaults
    // Runs during Phase 2 (management) when defaults is populated
    // OR during intentional cleanup (defaults was populated, now empty)
    //
    // SETTLED STATE DETECTION:
    // The hook tracks previous defaults keys to distinguish:
    // - "Never managed": Param in scopedParams but never in defaults → Preserve (might become managed)
    // - "Previously managed": Param was in defaults but no longer → Remove (was managed, now not)
    //
    // When `isSettled === true`, we know defaults has reached its final state and won't grow.
    // In this case, remove params in scopedParams but not in defaults (even if never in defaults).
    // This prevents params like `currentPage` from persisting when they shouldn't be in the URL.
    //
    const paramsToRemove = []
    const currentDefaultsKeys = new Set(Object.keys(defaults))

    // Cleanup: remove params in scopedParams but not in defaults
    // Prune params that are in scopedParams but not in managedParamKeys (defaults)
    if (!isDefaultsEmpty && (!isFirstRenderWithDefaults || isSettled)) {
      currentUrlParams.forEach((value, key) => {
        if (scopedParams.includes(key) && !(key in defaults)) {
          // Param is in scopedParams but not in defaults
          // (e.g., currentPage when needsPagination is false)
          if (isSettled) {
            // Defaults is settled - remove param even if never in defaults
            // We know defaults won't grow, so remove unmanaged params
            paramsToRemove.push(key)
            hasChanges = true
          } else if (previousDefaultsKeysRef.current.has(key)) {
            // Defaults not settled, but param was previously in defaults
            // This means it was previously managed but is no longer
            // (e.g., user switched from multi-site to single-site)
            paramsToRemove.push(key)
            hasChanges = true
          }
          // Else: Defaults not settled AND param was never in defaults
          // Preserve it - it might become managed when defaults grows
        }
      })

      // Update previous defaults keys for next render
      previousDefaultsKeysRef.current = currentDefaultsKeys
    } else if (isIntentionalEmpty) {
      // Intentional cleanup: defaults is empty AND isInitializing is explicitly false
      // OR defaults was populated, now empty - remove all scoped params
      // This handles cases like single site scenario where we don't need site param
      currentUrlParams.forEach((value, key) => {
        if (scopedParams.includes(key)) {
          paramsToRemove.push(key)
          hasChanges = true
        }
      })
    } else if (isFirstRenderWithDefaults) {
      // Update previous defaults keys for next render
      previousDefaultsKeysRef.current = currentDefaultsKeys
    } else {
      // Update previous defaults keys even if no cleanup (for tracking)
      previousDefaultsKeysRef.current = currentDefaultsKeys
    }

    // Step 3: Apply all updates in a single URL update
    // Only update if there are actual changes (updates or removals)
    if (
      hasChanges &&
      (Object.keys(updates).length > 0 || paramsToRemove.length > 0)
    ) {
      const newSearchParams = new URLSearchParams(location.search)

      // CRITICAL: Preserve all valid managed params that aren't being updated
      // This prevents valid URL params from being accidentally removed
      // Also preserve params that exist in URL but aren't in defaults yet (during initialization)
      if (shouldSet) {
        // First, preserve all params in defaults that aren't being updated
        if (Object.keys(defaults).length > 0) {
          Object.keys(defaults).forEach(key => {
            // If param exists in URL and is NOT in updates, it's valid and matches - preserve it
            if (currentUrlParams.has(key) && !(key in updates)) {
              const existingValue = currentUrlParams.get(key)
              // Keep the existing value - don't overwrite it
              newSearchParams.set(key, existingValue)
            }
          })
        }

        // Also preserve any params in scopedParams that exist in URL but aren't in defaults yet
        // This handles the case where defaults is empty during initialization
        scopedParams.forEach(key => {
          if (
            currentUrlParams.has(key) &&
            !(key in defaults) &&
            !(key in updates) &&
            !paramsToRemove.includes(key)
          ) {
            const existingValue = currentUrlParams.get(key)
            newSearchParams.set(key, existingValue)
          }
        })
      }

      // Apply managed param updates (only params that need changing)
      Object.keys(updates).forEach(key => {
        const value = updates[key]
        if (value !== null && value !== undefined) {
          newSearchParams.set(key, value.toString())
        } else {
          newSearchParams.delete(key)
        }
      })

      // Remove unmanaged params in scope
      paramsToRemove.forEach(key => {
        newSearchParams.delete(key)
      })

      // Only update if there are actual changes to the URL string
      const newSearch = newSearchParams.toString()
      const currentSearch = location.search.replace(/^\?/, '')

      if (newSearch !== currentSearch) {
        history.replace({ search: newSearch })
      }
    }
  }, [
    location.search,
    shouldSetDefaults,
    defaults,
    scopedParams,
    validateParam,
    history,
    isSettled,
  ])

  // ============================================
  // SIDE EFFECTS: Callback synchronization
  // ============================================
  // Call onParamsChange when managedParams change
  // This allows syncing to Redux, analytics, logging, etc.
  const prevManagedParamsRef = useRef({})
  useEffect(() => {
    if (!onParamsChange) return

    // Check if managedParams actually changed
    const prevParams = prevManagedParamsRef.current
    const currentKeys = Object.keys(managedParams)
    const prevKeys = Object.keys(prevParams)

    const hasChanged =
      currentKeys.length !== prevKeys.length ||
      currentKeys.some(key => managedParams[key] !== prevParams[key]) ||
      prevKeys.some(key => !(key in managedParams))

    if (hasChanged) {
      prevManagedParamsRef.current = { ...managedParams }
      onParamsChange(managedParams)
    }
  }, [managedParams, onParamsChange])

  return { managedParams, updateUrlParams, searchParams }
}
